import { defaultAuthConfig } from '../types/auth';
import { API_BASE_URL } from '../config/api';

interface ManagerPodServiceConfig {
  baseUrl: string;
  tokenKey: string;
  userKey: string;
}

type Query = Record<string, string | number | undefined>;

export type PodProjectStatus = 'draft' | 'under_review' | 'approved' | 'rejected';
export type PodPaymentStatus = 'pending' | 'paid' | 'failed';
export type PodPrintStatus = 'not_sent' | 'in_production' | 'shipped' | 'delivered' | 'cancelled';

export interface ManagerPodOverview {
  totalProjects: number;
  activeProjects: number;
  totalListings: number;
  activeListings: number;
  totalRequests: number;
  projectsByStatus: Record<PodProjectStatus, number>;
  requestsByPrintStatus: Record<PodPrintStatus, number>;
  requestsByPaymentStatus: Record<PodPaymentStatus, number>;
}

export interface ManagerPodDashboardResponse {
  overview: ManagerPodOverview;
  pagination: { page: number; limit: number; total: number; totalPages: number };
  pods: Array<{
    project: {
      id: string;
      title: string;
      description: string | null;
      status: PodProjectStatus;
      adminNotes: string | null;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
      creator: { id: string | null; fullname: string | null; username: string | null; email: string | null } | null;
    };
    listing: null | { id: string; price: number; currency: string; productImage: string | null; isActive: boolean };
    printSpec: null | {
      id: string;
      trimSize: string;
      pageCount: number;
      paperType: string;
      bindingType: string;
      colorType: string;
    };
    preflightStatus: 'missing_files' | 'pending' | 'failed' | 'passed';
    preview: {
      cover: null | { id: string; fileUrl: string; validationStatus: 'pending' | 'valid' | 'invalid'; versionNumber: number };
      interior: null | { id: string; fileUrl: string; validationStatus: 'pending' | 'valid' | 'invalid'; versionNumber: number };
    };
    linkedProducts: Array<{ id: string; titleKey: string; nameKey: string; slug: string; imageUrl: string | null; status: string }>;
    requests: {
      totalRequests: number;
      totalQuantity: number;
      totalRevenue: number;
      latestRequestAt: string | null;
      paymentStatus: Record<PodPaymentStatus, number>;
      printStatus: Record<PodPrintStatus, number>;
    };
  }>;
}

export interface ManagerPodRequestsResponse {
  summary: {
    totalRequests: number;
    totalQuantity: number;
    totalRevenue: number;
    latestRequestAt: string | null;
    paymentStatus: Record<PodPaymentStatus, number>;
    printStatus: Record<PodPrintStatus, number>;
  };
  pagination: { page: number; limit: number; total: number; totalPages: number };
  requests: Array<{
    id: string;
    quantity: number;
    totalPrice: number;
    paymentStatus: PodPaymentStatus;
    printStatus: PodPrintStatus;
    provider: string | null;
    externalPrintId: string | null;
    trackingNumber: string | null;
    trackingUrl: string | null;
    createdAt: string;
    updatedAt: string;
    user: { id: string | null; fullname: string | null; username: string | null; email: string | null } | null;
    listing: null | { id: string; projectId: string | null; price: number; currency: string; productImage: string | null };
    product: null | { id: string; titleKey: string; nameKey: string; slug: string; imageUrl: string | null };
  }>;
}

export interface ManagerPodPreviewResponse {
  project: ManagerPodDashboardResponse['pods'][number]['project'];
  listing: ManagerPodDashboardResponse['pods'][number]['listing'];
  printSpec: ManagerPodDashboardResponse['pods'][number]['printSpec'];
  preflightStatus: 'missing_files' | 'pending' | 'failed' | 'passed';
  preview: ManagerPodDashboardResponse['pods'][number]['preview'];
  fileVersions: Array<{
    id: string;
    fileType: 'cover' | 'interior';
    fileUrl: string;
    cloudinaryPublicId: string | null;
    versionNumber: number;
    validationStatus: 'pending' | 'valid' | 'invalid';
    validationReport: unknown;
    createdAt: string;
    updatedAt: string;
  }>;
  linkedProducts: ManagerPodDashboardResponse['pods'][number]['linkedProducts'];
  requestSummary: ManagerPodRequestsResponse['summary'];
  recentRequests: ManagerPodRequestsResponse['requests'];
  requestsPagination: ManagerPodRequestsResponse['pagination'];
}

const defaultManagerPodServiceConfig: ManagerPodServiceConfig = {
  baseUrl: API_BASE_URL,
  tokenKey: defaultAuthConfig.tokenKey,
  userKey: defaultAuthConfig.userKey,
};

class ManagerPodService {
  private baseUrl: string;
  private tokenKey: string;
  private userKey: string;

  constructor(config = defaultManagerPodServiceConfig) {
    this.baseUrl = config.baseUrl;
    this.tokenKey = config.tokenKey;
    this.userKey = config.userKey;
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  }

  private asString(value: unknown): string | undefined {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
    return undefined;
  }

  private getBaseCandidates(): string[] {
    const trimmedBase = this.baseUrl.replace(/\/+$/, '');
    const candidates: string[] = [];

    const add = (value: string) => {
      const normalized = value.replace(/\/+$/, '');
      if (normalized && !candidates.includes(normalized)) {
        candidates.push(normalized);
      }
    };

    add(trimmedBase);

    if (!/\/api(?:\/v1)?$/i.test(trimmedBase)) {
      add(`${trimmedBase}/api`);
      add(`${trimmedBase}/api/v1`);
    } else if (/\/api$/i.test(trimmedBase)) {
      add(`${trimmedBase}/v1`);
    } else if (/\/api\/v1$/i.test(trimmedBase)) {
      add(trimmedBase.replace(/\/api\/v1$/i, '/api'));
    }

    return candidates;
  }

  private buildQuery(query: Query = {}): string {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.set(key, String(value));
      }
    });
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }

  private buildHeaders(): HeadersInit {
    const headers: Record<string, string> = {
      Accept: '*/*',
    };

    if (typeof window === 'undefined') {
      return headers;
    }

    const token = window.localStorage.getItem(this.tokenKey);
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const storedUserRaw = window.localStorage.getItem(this.userKey);
    if (!storedUserRaw) {
      return headers;
    }

    try {
      const storedUser = this.asRecord(JSON.parse(storedUserRaw));
      const userId = this.asString(storedUser.id) || this.asString(storedUser._id);
      const userRole = this.asString(storedUser.role) || 'manager';
      if (userId) {
        headers['x-user-id'] = userId;
      }
      headers['x-user-role'] = userRole;
    } catch {
      // Ignore malformed cached user payload.
    }

    return headers;
  }

  private toErrorMessage(payload: unknown, fallback: string): string {
    const record = this.asRecord(payload);
    const dataRecord = this.asRecord(record.data);
    return (
      this.asString(record.message) ||
      this.asString(dataRecord.message) ||
      this.asString(record.error) ||
      this.asString(dataRecord.error) ||
      fallback
    );
  }

  private async request<T>(path: string): Promise<T> {
    const headers = this.buildHeaders();
    const candidates = this.getBaseCandidates();
    let lastError: Error | null = null;

    for (let index = 0; index < candidates.length; index += 1) {
      const base = candidates[index];
      const shouldRetryOnNotFound = index < candidates.length - 1;

      try {
        const response = await fetch(`${base}${path}`, { method: 'GET', headers });
        let payload: unknown = null;
        try {
          payload = await response.json();
        } catch {
          payload = null;
        }

        if (response.status === 404 && shouldRetryOnNotFound) {
          continue;
        }

        if (!response.ok) {
          const fallback = `Request failed with status ${response.status}`;
          throw new Error(this.toErrorMessage(payload, fallback));
        }

        const record = this.asRecord(payload);
        if (record.success === false) {
          throw new Error(this.toErrorMessage(payload, 'Request failed'));
        }

        return (record.data ?? payload) as T;
      } catch (error: unknown) {
        lastError = error instanceof Error ? error : new Error('Request failed');
      }
    }

    throw lastError ?? new Error('Unable to connect to manager POD API.');
  }

  async getDashboard(params: {
    page?: number;
    limit?: number;
    status?: PodProjectStatus;
    search?: string;
  }): Promise<ManagerPodDashboardResponse> {
    return this.request<ManagerPodDashboardResponse>(`/admin/pod/dashboard${this.buildQuery(params)}`);
  }

  async getProjectPreview(projectId: string): Promise<ManagerPodPreviewResponse> {
    return this.request<ManagerPodPreviewResponse>(`/admin/pod/dashboard/projects/${projectId}/preview`);
  }

  async getProjectRequests(
    projectId: string,
    params: {
      page?: number;
      limit?: number;
      paymentStatus?: PodPaymentStatus;
      printStatus?: PodPrintStatus;
    },
  ): Promise<ManagerPodRequestsResponse> {
    return this.request<ManagerPodRequestsResponse>(
      `/admin/pod/dashboard/projects/${projectId}/requests${this.buildQuery(params)}`,
    );
  }
}

export const managerPodService = new ManagerPodService();

